import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cloud, LogOut, Monitor, RotateCw } from 'lucide-react';
import { useIpcQuery } from '../hooks/useIpc';
import { appModeBlockedLabel, type FoundryAppMode } from '@volley/crucible-ipc-types';

function cloudUnavailableLabel(reason: string | undefined): string {
  switch (reason) {
    case 'flag-disabled':
      return 'Cloud Foundry is disabled for this build.';
    case 'missing-url':
      return 'Cloud Foundry URL is not configured.';
    case 'invalid-url':
      return 'Cloud Foundry URL is invalid.';
    case 'network':
      return 'Cloud Foundry could not be reached.';
    default:
      return 'Cloud Foundry is unavailable.';
  }
}

export function AppModeLauncher(): React.JSX.Element {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [pendingMode, setPendingMode] = useState<FoundryAppMode | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const snapshotQuery = useIpcQuery('app-mode:get-snapshot');
  const snapshot = snapshotQuery.data;
  const cloudDisabled = snapshot?.cloud.enabled === false;
  const cloudLoadFailed =
    snapshot?.cloud.loadState === 'load-failed' || snapshot?.cloud.loadState === 'unreachable';
  const sourceInvalid = snapshot?.source === 'invalid';
  const showCloudSignOut = snapshot?.mode === 'cloud' && snapshot.cloud.enabled;

  const chooseMode = async (mode: FoundryAppMode): Promise<void> => {
    setError(null);
    setPendingMode(mode);
    try {
      const result = await window.crucible.invoke('app-mode:set-mode', { mode });
      if (!result.ok) {
        setError(appModeBlockedLabel(result));
        return;
      }
      // Window recreation destroys this renderer; when the mode was already
      // active the shell stays on /app-mode and must leave the launcher.
      if (result.alreadyActive) {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPendingMode(null);
    }
  };

  const signOutOfCloud = async (): Promise<void> => {
    setError(null);
    setSigningOut(true);
    try {
      await window.crucible.invoke('app-mode:clear-cloud-session');
      await snapshotQuery.refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-crucible-bg px-8 py-10 text-crucible-fg">
      <main className="w-full max-w-[760px]">
        <div className="mb-8">
          <p className="mb-2 text-[13px] font-medium uppercase tracking-[0.08em] text-[var(--text-3)]">
            Foundry
          </p>
          <h1 className="text-[32px] font-semibold leading-tight tracking-normal text-[var(--text-1)]">
            Choose how to use Foundry
          </h1>
          {sourceInvalid && (
            <p className="mt-3 text-[14px] leading-6 text-[var(--text-2)]">
              Foundry could not read your previous app mode. Choose a mode to continue.
            </p>
          )}
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <button
            type="button"
            onClick={() => void chooseMode('cloud')}
            disabled={cloudDisabled || pendingMode !== null}
            className="group min-h-[168px] rounded-lg border border-white/[0.11] bg-white/[0.045] p-5 text-left transition hover:border-sky-300/55 hover:bg-sky-300/[0.08] disabled:cursor-not-allowed disabled:opacity-45"
          >
            <span className="mb-4 flex h-10 w-10 items-center justify-center rounded-md border border-sky-300/25 bg-sky-300/[0.12] text-sky-100">
              <Cloud size={20} strokeWidth={1.8} />
            </span>
            <span className="block text-[18px] font-semibold text-[var(--text-1)]">
              Continue in Cloud
            </span>
            <span className="mt-2 block text-[14px] leading-6 text-[var(--text-2)]">
              Build and manage cloud-hosted games. Requires sign-in.
            </span>
            {cloudDisabled && (
              <span className="mt-4 block text-[13px] leading-5 text-amber-200/90">
                {cloudUnavailableLabel(snapshot?.cloud.unavailableReason)}
              </span>
            )}
            {!cloudDisabled && cloudLoadFailed && (
              <span className="mt-4 block text-[13px] leading-5 text-amber-200/90">
                Could not open Cloud Foundry. Try again or continue locally.
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => void chooseMode('local')}
            disabled={pendingMode !== null}
            className="group min-h-[168px] rounded-lg border border-white/[0.11] bg-white/[0.045] p-5 text-left transition hover:border-emerald-300/50 hover:bg-emerald-300/[0.07] disabled:cursor-not-allowed disabled:opacity-45"
          >
            <span className="mb-4 flex h-10 w-10 items-center justify-center rounded-md border border-emerald-300/25 bg-emerald-300/[0.12] text-emerald-100">
              <Monitor size={20} strokeWidth={1.8} />
            </span>
            <span className="block text-[18px] font-semibold text-[var(--text-1)]">
              Continue on this Mac
            </span>
            <span className="mt-2 block text-[14px] leading-6 text-[var(--text-2)]">
              Use local projects, local previews, and existing GitHub auth.
            </span>
          </button>
        </div>

        {showCloudSignOut && (
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => void signOutOfCloud()}
              disabled={pendingMode !== null || signingOut}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-white/[0.12] bg-white/[0.04] px-3 text-[13px] font-medium text-[var(--text-2)] transition hover:border-white/[0.22] hover:text-[var(--text-1)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {signingOut ? <RotateCw size={14} className="animate-spin" /> : <LogOut size={14} />}
              Sign out of Cloud Foundry
            </button>
          </div>
        )}

        <div className="mt-5 min-h-6 text-[13px] text-[var(--text-3)]">
          {pendingMode !== null && (
            <span className="inline-flex items-center gap-2">
              <RotateCw size={14} className="animate-spin" />
              Opening {pendingMode === 'cloud' ? 'Cloud Foundry' : 'Local Foundry'}...
            </span>
          )}
          {error && <span className="text-red-200">{error}</span>}
          {!error && pendingMode === null && snapshotQuery.isLoading && (
            <span>Loading mode...</span>
          )}
        </div>
      </main>
    </div>
  );
}
